import { useCallback, useEffect, useRef, useState } from "react";
import PatientService from "../../../Services/PatientService";
import { listenUnreadMessagesByDoctor } from "../../../Services/chatService";

export function useUnreadCounts(doctorId) {
  const [unreadCounts, setUnreadCounts] = useState({});

  useEffect(() => {
    if (!doctorId) return;
    const unsubscribe = listenUnreadMessagesByDoctor(doctorId, setUnreadCounts);
    return () => unsubscribe();
  }, [doctorId]);

  return unreadCounts;
}

async function fetchPatientById(patientId) {
  const json = sessionStorage.getItem("login_result");
    const loggedUser = json ? JSON.parse(json) : null;
    const doctorId = loggedUser?.user?.userId;

  const response = await PatientService.consultedPatientList({
    userId:doctorId,
    patientId,
    status: 1,
  });
  return response?.data?.[0] ?? null;
}

/**
 * Resolves a list of patient IDs (from listenForPatientsList) into full
 * patient records.
 *
 * FIX vs original `fetchAllConvPat`: that loop awaited one PatientListFilter
 * call per patient ID sequentially inside a for...of, meaning a doctor with
 * 30 conversations made 30 back-to-back network round trips before the
 * conversed list could render. This version:
 *   - fetches in parallel (Promise.all)
 *   - caches by patientId so re-renders don't re-fetch already-resolved
 *     patients
 *   - guards against race conditions: if patientIds changes again before
 *     an in-flight batch resolves, the stale result is discarded
 */
export function useResolvedPatients(patientIds) {
  const [patients, setPatients] = useState([]);
  const cacheRef = useRef(new Map());

  useEffect(() => {
    if (!patientIds || patientIds.length === 0) {
      setPatients([]);
      return;
    }

    let cancelled = false;

    const resolve = async () => {
      const idsToFetch = patientIds.filter((id) => !cacheRef.current.has(id));

      if (idsToFetch.length > 0) {
        const results = await Promise.all(
          idsToFetch.map(async (id) => {
            try {
              return await fetchPatientById(id);
            } catch (e) {
              console.error("[useResolvedPatients] failed for", id, e);
              return null;
            }
          })
        );

        results.forEach((patient, i) => {
          if (patient) cacheRef.current.set(idsToFetch[i], patient);
        });
      }

      if (cancelled) return;

      const resolved = patientIds
        .map((id) => cacheRef.current.get(id))
        .filter(Boolean);
      setPatients(resolved);
    };

    resolve();

    return () => {
      cancelled = true;
    };
  }, [patientIds]);

  return patients;
}

/**
 * Search-driven "all patients" lookup, used by the all-patients view.
 * Debouncing is handled by the caller passing an already-debounced search
 * term; this hook just owns the fetch + loading/error state.
 */
export function usePatientSearch(searchTerm, minLength = 4) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!searchTerm || searchTerm.length < minLength) {
      setResults([]);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const json = sessionStorage.getItem("login_result");
    const loggedUser = json ? JSON.parse(json) : null;
    const doctorId = loggedUser?.user?.userId;

    PatientService.consultedPatientList({userId:doctorId, patientName: searchTerm, status: 1 })
      .then((response) => {
        if (cancelled) return;
        setResults(response?.data || []);
      })
      .catch((e) => {
        if (cancelled) return;
        console.error("[usePatientSearch] failed:", e);
        setError("Could not search patients. Please try again.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [searchTerm, minLength]);

  return { results, loading, error };
}